// src/component/RelatedServicesCarousel.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { RelatedServiceItem } from "./RelatedServiceItem.jsx";

function getColsByWidth(w) {
  if (w >= 1024) return 4;
  if (w >= 640) return 3;
  return 2;
}

export default function RelatedServicesCarousel({
  items = [],
  title = "Related Services",
  onItemClick,
  activeUuid,
}) {
  const { t } = useTranslation();
  const safeItems = useMemo(() => (Array.isArray(items) ? items : []), [items]);

  const [cols, setCols] = useState(() =>
    typeof window !== "undefined" ? getColsByWidth(window.innerWidth) : 2
  );
  const [page, setPage] = useState(0);

  const pageCount = useMemo(() => {
    if (!safeItems.length) return 0;
    return Math.ceil(safeItems.length / cols);
  }, [safeItems.length, cols]);

  const showNav = safeItems.length > cols && pageCount > 1;

  // ✅ 현재 선택된 uuid가 몇 번째인지
  const activeIndex = useMemo(() => {
    if (!activeUuid) return -1;
    return safeItems.findIndex((it) => it?.uuid === activeUuid);
  }, [safeItems, activeUuid]);

  // ✅ 선택된 uuid가 속한 페이지로 자동 이동
  useEffect(() => {
    if (activeIndex < 0) return;
    const activePage = Math.floor(activeIndex / cols);
    setPage((p) => (p === activePage ? p : activePage));
  }, [activeIndex, cols]);

  useEffect(() => {
    const onResize = () => setCols(getColsByWidth(window.innerWidth));
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    setPage((p) => Math.min(p, Math.max(0, pageCount - 1)));
  }, [pageCount]);

  const canPrev = page > 0;
  const canNext = pageCount > 0 && page < pageCount - 1;

  const goPrev = () => setPage((p) => Math.max(0, p - 1));
  const goNext = () => setPage((p) => Math.min(pageCount - 1, p + 1));

  if (!safeItems.length) return null;

  return (
    <section className="mt-8">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-heading-sm text-text-primary">{title}</h2>

        <div className="flex items-center gap-2">
          <div className="text-body-sm text-text-tertiary">{t("common.items_count", { count: safeItems.length })}</div>

          {showNav ? (
            <>
              <button
                type="button"
                onClick={goPrev}
                disabled={!canPrev}
                aria-label="Previous"
                className={`inline-flex items-center justify-center rounded-xl border px-3 py-2 bg-white
                ${canPrev ? "hover:bg-bg-secondary" : "opacity-40 cursor-not-allowed"}`}
              >
                <span className="material-symbols-outlined text-[20px] leading-none">chevron_left</span>
              </button>

              <button
                type="button"
                onClick={goNext}
                disabled={!canNext}
                aria-label="Next"
                className={`inline-flex items-center justify-center rounded-xl border px-3 py-2 bg-white
                ${canNext ? "hover:bg-bg-secondary" : "opacity-40 cursor-not-allowed"}`}
              >
                <span className="material-symbols-outlined text-[20px] leading-none">chevron_right</span>
              </button>
            </>
          ) : null}
        </div>
      </div>

      <div className="mt-4 overflow-hidden">
        <div
          className="flex transition-transform duration-300 ease-out"
          style={{ transform: `translateX(-${page * 100}%)` }}
        >
          {Array.from({ length: pageCount }).map((_, pageIndex) => {
            const start = pageIndex * cols;
            const pageItems = safeItems.slice(start, start + cols);

            return (
              <div key={pageIndex} className="flex-shrink-0 w-full">
                <div
                  className="grid gap-4"
                  style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}
                >
                  {pageItems.map((item, idx) => {
                    const itemUuid = item?.uuid;
                    const isActive = !!activeUuid && itemUuid === activeUuid;

                    return (
                      <div key={itemUuid ?? `${pageIndex}-${idx}`} className="min-w-0">
                        <RelatedServiceItem
                          item={item}
                          isActive={isActive}
                          onClick={() => {
                            if (!itemUuid) return;

                            if (isActive) return;

                            onItemClick?.(item);
                          }}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
